import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { isFreeTemplateCode, isFreeVersion, isReportManager, useCollaboration, useReport, useReportMutations, useReportPulse, useTemplateVersion } from '../api/hooks'
import type { FindingDto, FindingSeverity, FormInstanceDto, PhotoDto, Question, TemplateVersion } from '../api/types'
import { problemOf } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Modal, RevBadge, SecureImage, fmtDateTime, severityColor } from '../components/ui'
import DateInput from '../components/DateInput'
import CollaborationPanel from '../components/CollaborationPanel'

/**
 * Web form filling — same engine as mobile: autosave, quick answers, ordered photos (drag & drop), findings.
 * Shared reports: approved participants answer next to the owner and see each other's changes within seconds (pulse polling);
 * only the owner (or a tenant admin) completes the report.
 */
export default function FillReportPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const report = useReport(id)
  const version = useTemplateVersion(report.data?.templateVersionId)
  const m = useReportMutations(id)
  const pulse = useReportPulse(id, report.data?.status === 'Draft')
  const collab = useCollaboration(id, !!report.data)
  const [groupId, setGroupId] = useState<string>('all')
  const [finding, setFinding] = useState<{ questionId: string; existing?: FindingDto } | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const pending = useRef(new Map<string, { selectedOptionCode?: string | null; comment?: string | null }>())
  const timer = useRef<number | null>(null)

  const r = report.data
  const v = version.data
  const questions = useMemo(() => [...(v?.questions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [v])
  const mainGroups = useMemo(() => (v?.groups ?? []).filter((g) => !g.parentGroupId).sort((a, b) => a.sortOrder - b.sortOrder), [v])
  const answers = useMemo(() => new Map((r?.answers ?? []).map((a) => [a.questionId, a])), [r])
  const groupOf = useCallback((q: Question) => {
    const g = v?.groups.find((x) => x.id === q.groupId)
    return g?.parentGroupId ? v?.groups.find((x) => x.id === g.parentGroupId) : g
  }, [v])
  const visible = useMemo(() => (groupId === 'all' ? questions : questions.filter((q) => groupOf(q)?.id === groupId)), [questions, groupId, groupOf])

  const flush = useCallback(() => {
    if (pending.current.size === 0) return
    const batch = [...pending.current.entries()].map(([questionId, x]) => {
      const current = answers.get(questionId)
      return { questionId, selectedOptionCode: x.selectedOptionCode !== undefined ? x.selectedOptionCode : current?.selectedOptionCode ?? null, comment: x.comment !== undefined ? x.comment : current?.comment ?? null }
    })
    pending.current.clear()
    setSaveState('saving')
    m.saveAnswers.mutate(batch, { onSuccess: () => setSaveState('saved'), onError: () => setSaveState('error') })
  }, [answers, m.saveAnswers])

  const queue = useCallback((questionId: string, patch: { selectedOptionCode?: string | null; comment?: string | null }) => {
    pending.current.set(questionId, { ...pending.current.get(questionId), ...patch })
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(flush, 700)
  }, [flush])

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  // Free-form reports ("Boş Rapor") have no questions: they are edited on their own page.
  if (r && (isFreeTemplateCode(r.templateCode) || isFreeVersion(v))) return <Navigate to={`/reports/${id}/free`} replace />
  if (report.isLoading || version.isLoading) return <p>{t('common.loading')}</p>
  if (!r || !v) return <p className="error">{t('common.loadFailed')}</p>
  if (pulse.lost) return <div className="card stack"><p style={{ margin: 0 }}>{t('collab.accessLost')}</p><div><Link className="button" to="/reports">{t('nav.reports')}</Link></div></div>
  if (r.status !== 'Draft') return <div className="card"><p>{t('fill.notDraft')}</p><Link className="button" to={`/reports/${id}`}>{t('reports.open')}</Link></div>

  const answered = r.answers.filter((a) => a.selectedOptionCode).length
  const options = [...v.answerOptions].sort((a, b) => a.sortOrder - b.sortOrder)
  // Participants contribute; completing belongs to the report's owner (or a tenant admin).
  const manager = isReportManager(user, r)
  const ownerName = collab.data?.ownerName

  return (
    <div className="fill">
      <aside className="fill-nav card">
        <div className="small"><b>{r.code ?? r.templateCode}</b> <RevBadge revisionNo={r.revisionNo} /> · {r.companyName}</div>
        <div className="progress"><div style={{ width: `${questions.length ? (answered / questions.length) * 100 : 0}%` }} /></div>
        <div className="muted small">{answered}/{questions.length} · {saveState === 'saving' ? t('fill.saving') : saveState === 'saved' ? t('fill.saved') : saveState === 'error' ? t('fill.saveError') : ''}</div>
        <button className={`nav-item ${groupId === 'all' ? 'active' : ''}`} onClick={() => setGroupId('all')}>{t('common.all')}</button>
        {mainGroups.map((g) => {
          const qs = questions.filter((q) => groupOf(q)?.id === g.id)
          const done = qs.filter((q) => answers.get(q.id)?.selectedOptionCode).length
          return <button key={g.id} className={`nav-item ${groupId === g.id ? 'active' : ''}`} onClick={() => setGroupId(g.id)}>{g.number ?? g.code}. {g.name} <span className="muted">{done}/{qs.length}</span></button>
        })}
        {r.score ? <div className="small" style={{ marginTop: 8 }}>{t('form.score')}: <b>%{r.score.percent} {r.score.grade}</b></div> : null}
        <div className="stack" style={{ marginTop: 12 }}>
          {manager ? <button onClick={() => { flush(); if (confirm(t('reports.completeConfirm'))) m.complete.mutate(false, { onSuccess: () => navigate(`/reports/${id}`) }) }} disabled={m.complete.isPending}>{t('reports.complete')}</button> : null}
          <Link className="button secondary" to={`/reports/${id}`}>{t('reports.open')}</Link>
        </div>
        {m.complete.isError ? <p className="error small">{problemOf(m.complete.error).title}</p> : null}
      </aside>

      <div className="stack">
        {!manager ? <p className="banner" role="note">{ownerName ? t('collab.participantBanner', { name: ownerName }) : t('collab.participantBannerNoName')}</p> : null}
        <CollaborationPanel reportId={id} status={r.status} live={pulse.live} />
        {visible.map((q) => (
          <QuestionCard key={q.id} q={q} r={r} v={v} options={options} onAnswer={(code) => queue(q.id, { selectedOptionCode: code })} onComment={(c) => queue(q.id, { comment: c })}
            onAddFinding={() => setFinding({ questionId: q.id })} onEditFinding={(f) => setFinding({ questionId: q.id, existing: f })} m={m} />
        ))}
      </div>

      {finding ? <FindingModal r={r} v={v} questionId={finding.questionId} existing={finding.existing} onClose={() => setFinding(null)} m={m} /> : null}
    </div>
  )
}

function QuestionCard({ q, r, v, options, onAnswer, onComment, onAddFinding, onEditFinding, m }: {
  q: Question; r: FormInstanceDto; v: TemplateVersion; options: TemplateVersion['answerOptions']
  onAnswer: (code: string) => void; onComment: (c: string) => void; onAddFinding: () => void; onEditFinding: (f: FindingDto) => void; m: ReturnType<typeof useReportMutations>
}) {
  const { t } = useTranslation()
  const answer = r.answers.find((a) => a.questionId === q.id)
  const [selected, setSelected] = useState(answer?.selectedOptionCode ?? '')
  const [comment, setComment] = useState(answer?.comment ?? '')
  const commentFocused = useRef(false)
  const commentDirty = useRef(false) // typed here and not yet confirmed by the server
  const [drag, setDrag] = useState(false)
  const photos = r.photos.filter((p) => p.questionId === q.id).sort((a, b) => a.sortOrder - b.sortOrder)
  const findings = r.findings.filter((f) => f.questionId === q.id)
  const sub = v.groups.find((g) => g.id === q.groupId)

  useEffect(() => { setSelected(answer?.selectedOptionCode ?? '') }, [answer?.selectedOptionCode])

  // Live refresh: take over a comment somebody else wrote, but never over what is being typed here (focused or still unsaved).
  const serverComment = answer?.comment ?? ''
  const adoptComment = useCallback((value: string) => {
    setComment((current) => {
      if (current.trim() === value.trim()) { commentDirty.current = false; return current } // the server trims; what was typed has arrived
      return commentFocused.current || commentDirty.current ? current : value
    })
  }, [])
  useEffect(() => { adoptComment(serverComment) }, [serverComment, adoptComment])

  async function upload(files: FileList | File[]) {
    const list = [...files].filter((f) => f.type.startsWith('image/'))
    for (const file of list) await m.uploadPhoto.mutateAsync({ file, questionId: q.id })
  }
  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDrag(false)
    void upload(e.dataTransfer.files)
  }
  function move(p: PhotoDto, dir: -1 | 1) {
    const ordered = [...photos]
    const i = ordered.findIndex((x) => x.id === p.id)
    const j = i + dir
    if (j < 0 || j >= ordered.length) return
    ;[ordered[i], ordered[j]] = [ordered[j], ordered[i]]
    m.reorderPhotos.mutate(ordered.map((x, idx) => ({ photoId: x.id, sortOrder: idx + 1 })))
  }

  return (
    <section className="card question" id={`q-${q.questionNo}`}>
      <div className="row between wrap">
        <div><b>{q.questionNo}.</b> {q.text}</div>
        <div className="muted small">{sub?.code}{q.maxPoints > 0 ? ` · ${q.maxPoints} p` : ''}</div>
      </div>
      <div className="row wrap options">
        {options.map((o) => (
          <button key={o.code} className={`option ${selected === o.code ? 'selected' : ''}`} style={{ borderColor: o.color, background: selected === o.code ? o.color : undefined }} onClick={() => { setSelected(o.code); onAnswer(o.code); if (o.createsFinding && findings.length === 0) onAddFinding() }}>
            {o.label}
          </button>
        ))}
      </div>
      <textarea
        placeholder={t('form.commentPlaceholder')}
        value={comment}
        onChange={(e) => { commentDirty.current = true; setComment(e.target.value); onComment(e.target.value) }}
        onFocus={() => { commentFocused.current = true }}
        onBlur={() => { commentFocused.current = false; adoptComment(serverComment) }}
        rows={2}
      />

      <div className={`dropzone ${drag ? 'drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
        <div className="row between wrap">
          <span className="muted small">{t('fill.dropHint')}</span>
          <label className="button secondary small-btn">{t('form.pickPhoto')}<input type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files)} /></label>
        </div>
        {photos.length > 0 ? (
          <div className="thumbs">
            {photos.map((p, i) => (
              <figure key={p.id}>
                <SecureImage fileId={p.thumbnailFileId ?? p.fileId} alt="" className="thumb" />
                <figcaption className="small">{i + 1}/{photos.length} <span className="muted">{fmtDateTime(p.capturedAt)}</span></figcaption>
                <div className="row" style={{ gap: 2 }}>
                  <button className="ghost" onClick={() => move(p, -1)} disabled={i === 0} aria-label="left">◀</button>
                  <button className="ghost" onClick={() => move(p, 1)} disabled={i === photos.length - 1} aria-label="right">▶</button>
                  <button className="ghost" onClick={() => confirm(t('common.deleteConfirm')) && m.deletePhoto.mutate(p.id)} aria-label="delete">✕</button>
                </div>
              </figure>
            ))}
          </div>
        ) : null}
      </div>

      <div className="row between wrap">
        <span className="small">{t('form.findings')} ({findings.length})</span>
        <button className="secondary small-btn" onClick={onAddFinding}>{t('form.addFinding')}</button>
      </div>
      {findings.map((f) => (
        <button key={f.id} className="finding-row" onClick={() => onEditFinding(f)}>
          <Badge color={severityColor[f.severity]}>{t(`severity.${f.severity}`)}</Badge> <span>{f.description}</span>
        </button>
      ))}
      {q.requiresPhotoOnFinding && findings.length > 0 && photos.length === 0 ? <p className="error small">{t('form.photoRequired')}</p> : null}
    </section>
  )
}

function FindingModal({ r, v, questionId, existing, onClose, m }: { r: FormInstanceDto; v: TemplateVersion; questionId: string; existing?: FindingDto; onClose: () => void; m: ReturnType<typeof useReportMutations> }) {
  const { t } = useTranslation()
  const q = v.questions.find((x) => x.id === questionId)
  const answer = r.answers.find((a) => a.questionId === questionId)
  const [severity, setSeverity] = useState<FindingSeverity>(existing?.severity ?? q?.defaultSeverity ?? 'Minor')
  const [description, setDescription] = useState(existing?.description ?? answer?.comment ?? '')
  const [actionText, setActionText] = useState(existing?.actionText ?? q?.standardActionText ?? '')
  const [responsible, setResponsible] = useState(existing?.responsibleName ?? '')
  const [dueDate, setDueDate] = useState(existing?.dueDate ?? '')
  const [openAction, setOpenAction] = useState(existing?.openAction ?? true)

  function save() {
    m.upsertFinding.mutate({ id: existing?.id, questionId, severity, description, actionText: actionText || null, openAction, responsibleName: responsible || null, dueDate: dueDate || null }, { onSuccess: onClose })
  }

  return (
    <Modal title={`${t('finding.title')} · S.${q?.questionNo ?? ''}`} onClose={onClose}>
      <div className="stack">
        <div className="row wrap">
          {(['Minor', 'Major', 'ZeroTolerance'] as FindingSeverity[]).map((s) => (
            <button key={s} className={`option ${severity === s ? 'selected' : ''}`} style={{ borderColor: severityColor[s], background: severity === s ? severityColor[s] : undefined }} onClick={() => setSeverity(s)}>{t(`severity.${s}`)}</button>
          ))}
        </div>
        <label>{t('finding.description')}<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} /></label>
        <label>{t('finding.action')}<textarea value={actionText} onChange={(e) => setActionText(e.target.value)} rows={4} /></label>
        <div className="grid two">
          <label>{t('finding.responsible')}<input value={responsible} onChange={(e) => setResponsible(e.target.value)} placeholder={r.companyName} /></label>
          <label>{t('finding.dueDate')}<DateInput value={dueDate} onChange={setDueDate} /></label>
        </div>
        <label className="row"><input type="checkbox" checked={openAction} onChange={(e) => setOpenAction(e.target.checked)} /> {t('finding.openAction')}</label>
        {m.upsertFinding.isError ? <p className="error small">{problemOf(m.upsertFinding.error).title}</p> : null}
        <div className="row between">
          {existing ? <button className="danger" onClick={() => confirm(t('finding.deleteConfirm')) && m.deleteFinding.mutate(existing.id, { onSuccess: onClose })}>{t('common.delete')}</button> : <span />}
          <div className="row">
            <button className="secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button onClick={save} disabled={!description.trim() || m.upsertFinding.isPending}>{t('common.save')}</button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
